import React, { useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import useAnchoredLayer from "./useAnchoredLayer";

const DROPDOWN_WIDTH = 180; // px — matches min-width in CSS

/**
 * ActionMenu — the row/card overflow menu.
 *
 * ⚠️ `label` is the trigger's accessible name. The trigger is an icon-only
 * button, so without it every one of these announces as "button" and a page of
 * them is a column of identical unnamed controls. It defaults to "Actions" so
 * no existing call site loses anything; pass the row's subject
 * (`Actions for "Diwali intake"`) wherever there is more than one on screen.
 *
 * The trigger is a "⋮" unless `trigger` says otherwise. It had no default
 * until 2026-10-19, and every call site that left `trigger` out — Branches,
 * Asset categories, Website → Team, Chat reports, Compliance, the syllabus
 * builder — drew an empty 32px button nobody could see.
 *
 * ⚠️ The dropdown closes on ANY click inside it, which is right for a menu of
 * commands and wrong for anything with a field in it. A panel the user types
 * into is `ColumnFilter`'s shape, not this one — both sit on
 * `useAnchoredLayer`, which is where the placement they share now lives.
 */
const ActionMenu = ({ children, trigger, label = "Actions" }) => {
  const [open, setOpen] = useState(false);
  const { anchorRef, layerRef, style } = useAnchoredLayer({
    open,
    onClose: () => setOpen(false),
    width: DROPDOWN_WIDTH,
  });

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <div className="app-action-menu">
      <button
        ref={anchorRef}
        type="button"
        className="app-action-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleTriggerClick}
      >
        {trigger ?? <MoreVertical size={16} aria-hidden="true" />}
      </button>
      {open && createPortal(
        <div
          ref={layerRef}
          className="app-action-dropdown"
          style={style}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

export const ActionMenuItem = ({ children, onClick, danger = false, disabled = false }) => (
  <button
    type="button"
    className={`app-action-item${danger ? " app-action-item--danger" : ""}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

export const ActionMenuDivider = () => <div className="app-action-divider" />;

export default ActionMenu;
